/**
 * Por quais veículos este aparelho já passou — a vida dele, em ordem.
 *
 * O dado sempre esteve guardado (transferir FECHA o vínculo em vez de sobrescrevê-lo), mas não
 * havia por onde vê-lo: só a instalação vigente era exposta. Na prática, transferir o rastreador
 * apagava da vista o passado dele, embora o banco o preservasse inteiro.
 *
 * Cada linha leva ao trajeto daquele carro. E a contagem de posições é DO PERÍODO, não do veículo:
 * o mesmo carro pode receber o aparelho duas vezes, e somar tudo faria a primeira passagem parecer
 * maior do que foi.
 */
import { Car, History, PowerOff } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Chip, Spinner } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

type Periodo = {
  instalacao_id: string;
  veiculo_id: string;
  placa: string | null;
  marca: string | null;
  modelo: string | null;
  inicio_em: string;
  fim_em: string | null;
  vigente: boolean;
  rastreamento_ativo: boolean;
  posicoes: number;
};

const dia = (iso: string) => new Date(iso).toLocaleDateString('pt-BR');

/** Quanto durou a passagem — em dias, que é a unidade em que se pensa uma instalação. */
function duracao(inicio: string, fim: string | null): string {
  const ms = (fim ? new Date(fim).getTime() : Date.now()) - new Date(inicio).getTime();
  const dias = Math.max(0, Math.round(ms / 86_400_000));
  if (dias === 0) return 'menos de um dia';
  return dias === 1 ? '1 dia' : `${dias} dias`;
}

export function PainelHistoricoRastreador({
  rastreadorId,
  recarregar,
}: {
  rastreadorId: string;
  recarregar: () => Promise<void>;
}) {
  const [periodos, setPeriodos] = useState<Periodo[] | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [encerrando, setEncerrando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    const r = await api.get<Periodo[]>(`/cadastro/rastreadores/${rastreadorId}/instalacoes`);
    setCarregando(false);
    if (r.ok) setPeriodos(r.dados);
    else toast.error(r.erro);
  }, [rastreadorId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function encerrar() {
    setEncerrando(true);
    const r = await api.post(`/cadastro/rastreadores/${rastreadorId}/encerrar`);
    setEncerrando(false);
    if (!r.ok) return void toast.error(r.erro);
    toast.success('Instalação encerrada. O histórico do veículo continua intacto.');
    await carregar();
    await recarregar();
  }

  const vigente = periodos?.find((p) => p.vigente);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="size-4 text-accent" />
          Por onde este aparelho já passou
        </CardTitle>
        <p className="text-[13px] leading-[1.5] text-text-secondary">
          Transferir não apaga nada: cada passagem vira um período, e o que foi gravado em cada um
          continua sendo do carro daquela época.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {carregando ? (
          <Spinner />
        ) : !periodos?.length ? (
          <p className="tipo-corpo-pequeno text-text-tertiary">
            Este rastreador nunca foi instalado em veículo nenhum.
          </p>
        ) : (
          <div className="flex flex-col">
            {periodos.map((p) => (
              <div
                key={p.instalacao_id}
                className={cn(
                  'flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border py-3 first:border-t-0',
                  !p.vigente && 'opacity-75',
                )}
              >
                <Car className="size-4 shrink-0 text-text-tertiary" />
                <Link
                  to={`/cadastro/veiculos/${p.veiculo_id}`}
                  className="tipo-dado text-text hover:text-accent hover:underline"
                >
                  {p.placa ?? 'sem placa'}
                </Link>
                <span className="tipo-corpo-pequeno text-text-secondary">
                  {[p.marca, p.modelo].filter(Boolean).join(' ')}
                </span>
                <span className="tipo-corpo-pequeno text-text-tertiary">
                  {dia(p.inicio_em)} — {p.fim_em ? dia(p.fim_em) : 'hoje'} ·{' '}
                  {duracao(p.inicio_em, p.fim_em)}
                </span>
                <span className="tipo-corpo-pequeno text-text-tertiary">
                  {p.posicoes.toLocaleString('pt-BR')} posições
                </span>
                {p.vigente ? (
                  <Chip
                    className={
                      p.rastreamento_ativo
                        ? 'border-success/40 text-success'
                        : 'border-warning/40 text-warning'
                    }
                  >
                    {p.rastreamento_ativo ? 'aqui agora' : 'aqui, parado'}
                  </Chip>
                ) : (
                  <Chip>encerrado</Chip>
                )}
              </div>
            ))}
          </div>
        )}

        {vigente && (
          <div className="flex flex-wrap items-center gap-3 border-t border-border pt-3">
            <Button size="sm" variant="fantasma" disabled={encerrando} onClick={() => void encerrar()}>
              {encerrando ? <Spinner /> : <PowerOff />} Tirar do veículo
            </Button>
            <span className="tipo-corpo-pequeno text-text-tertiary">
              Fecha o período sem pôr o aparelho em outro carro — para quando ele volta para a
              gaveta. Nada do que foi gravado se perde.
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
